'use client';

import { getFragmentData } from '@/__generated__';
import { EventEntryFragment } from '@/__generated__/graphql';
import { EVENT_LABEL_CATEGORY_FRAGMENT } from '@/gql/fragments/categories.gql';
import { ENTRY_BASE_FRAGMENT } from '@/gql/fragments/entries.gql';
import { EVENT_CARD_FRAGMENT } from '@/gql/fragments/entryCards.gql';
import { useEventTab } from '@/layouts/EventLayout/EventLayout.Body';
import { parseProgram } from '@/lib/parsers/categories';
import { useColorScheme } from '@/lib/parsers/common';
import useShortlistStore, { useIsShortlisted } from '@/lib/store/shortlist';
import { excludeEmptyTypedElements, firstNonNullable } from '@liquorice/utils';
import { useTranslations } from 'next-intl';
import Breadcrumbs from '../Breadcrumbs';
import IconButton from '../IconButton';
import PageTicket from '../PageTicket';
import Box from '../ui/Box';
import Btn from '../ui/Btn';
import Container from '../ui/Container';
import Grid from '../ui/Grid';
import * as styles from './PageHeader.css';
import PageHeaderRoot from './PageHeaderRoot';
import { PageLabel } from './PageLabel';
import PageMedia from './PageMedia';
import PageSubTitle from './PageSubTitle';
import PageTitle from './PageTitle';

const PageHeaderEvent = (props: EventEntryFragment) => {
  const t = useTranslations();

  const card = getFragmentData(EVENT_CARD_FRAGMENT, props);
  const base = getFragmentData(ENTRY_BASE_FRAGMENT, card);

  const { entryImage, entryTitle, entryAdditionalTitle, entrySubTitle, videoUrl } = card;
  const { programCategory, eventLabelCategory } = props;

  const isExpired = base.status === 'expired';

  const { value, toggle } = useEventTab();

  const toggleEvent = useShortlistStore((s) => s.toggleEvent);
  const isShortlisted = useIsShortlisted(card);

  const program = parseProgram(programCategory);

  const eventLabel = firstNonNullable(
    getFragmentData(EVENT_LABEL_CATEGORY_FRAGMENT, excludeEmptyTypedElements(eventLabelCategory))
  );

  const { colorScheme: programColorScheme } = program ?? {};
  const { colorScheme, oppositeScheme } = useColorScheme(programColorScheme, 'pink');

  const defaultScheme = colorScheme || 'pink';
  const colorSet = isExpired ? 'error' : defaultScheme;

  return (
    <PageHeaderRoot
      variant="event"
      colorSet={colorSet}
      paper
      GridProps={{ cx: { rowGap: 'md', pT: 'xl', pB: 'xl' } }}
      GridColProps={{ md: 8 }}
      beforeContainer={
        <Box className={styles.eventHero}>
          <Box cx={{ width: 'full', height: 'full' }}>
            <PageMedia
              VideoProps={{ videoUrl }}
              ImageProps={{
                priority: true,
                fixedRatio: true,
                className: styles.eventHeroImage,
                data: entryImage,
              }}
            />
          </Box>
          <Container cx={{ position: 'relative' }}>
            {(eventLabel?.title || isExpired) && (
              <PageLabel color={colorSet} paper>
                {isExpired ? t('entryIndex.pastEvent') : eventLabel?.title}
              </PageLabel>
            )}
            <IconButton
              elevation="button"
              color="white"
              variant="text"
              icon={isShortlisted ? 'heart' : 'heartOutlined'}
              cx={{ mY: 'sm', mX: 'md' }}
              className={styles.eventShortlistButton}
              onClick={() => toggleEvent(card)}
            />
          </Container>
        </Box>
      }
      afterCol={
        <Grid.Col md={4}>
          <PageTicket data={card} />
        </Grid.Col>
      }
      afterContainer={
        <Container>
          <Grid>
            <Grid.Col sm={8} cx={{ display: 'flex', direction: 'row', columnGap: '6xs' }}>
              <Btn
                color={!value ? 'white' : oppositeScheme}
                disableHover
                className={styles.eventBodyToggleButton}
                onClick={() => toggle(false)}>
                {t('common.description')}
              </Btn>
              <Btn
                color={value ? 'white' : oppositeScheme}
                disableHover
                className={styles.eventBodyToggleButton}
                onClick={() => toggle(true)}>
                {t('common.faqs')}
              </Btn>
            </Grid.Col>
          </Grid>
        </Container>
      }>
      <Breadcrumbs
        data={base}
        cx={{ mB: 'xs' }}
        CtaButtonProps={{ color: isExpired ? 'white' : oppositeScheme }}
        TxtProps={{ weight: 'bold' }}
      />
      {entryTitle && (
        <PageTitle variant="h2" cx={{ mB: '3xs' }}>
          {entryTitle}
        </PageTitle>
      )}
      {entryAdditionalTitle && (
        <PageTitle variant="h4" cx={{ mB: '3xs' }}>
          {entryAdditionalTitle}
        </PageTitle>
      )}
      {entrySubTitle && (
        <PageSubTitle useGridCol={false} variant="body">
          {entrySubTitle}
        </PageSubTitle>
      )}
    </PageHeaderRoot>
  );
};

export default PageHeaderEvent;
